// =========================================================================
// SellShell — vendor portal chrome.
//
// Layout decisions (this is a busy app with 10 top-level surfaces):
//
//   * Desktop (>= lg / 1024px): logo on the left, FIVE inline primary
//     tabs (Dashboard, Products, Orders, Inbox, Help) + a `More ▾`
//     dropdown that holds the lower-frequency surfaces (Purchase orders,
//     Returns, Advertising, Team, Profile). Inlining all ten made the
//     bar look "crammed" (operator feedback, 2026-05-20). Five tabs +
//     overflow is the standard pattern for busy back-office apps and
//     gives every label room to breathe. Help stays inline because it
//     carries the unread badge.
//
//   * Tablet + phone (< lg): nav collapses into a hamburger drawer (the
//     shared `<Drawer>` primitive). The drawer keeps the FLAT 10-item
//     list — the drawer is already vertical so there's no cramming, and
//     a "More" expander inside a drawer would just add a click.
//
//   * Account chrome (was four separate elements: storefront pill, switch
//     icon, sign-out button, email) collapses into ONE round initial
//     button at the top-right. Click -> dropdown with email, storefront
//     status, "Switch storefront" (if the user owns more than one), and
//     "Sign out". This is the operator-loved control; do not change.
//
// Removed: horizontal `overflow-x-auto` on the nav row. That's never the
// right answer for a primary nav -- it makes the bar feel broken (a stray
// trackpad scroll silently hides links).
// =========================================================================

import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FooterLanguagePicker, useApplyAccountLocale } from '@pc/i18n';
import { useQuery } from '@tanstack/react-query';
import {
  ShopMessageNotificationWatcher,
  VendorSuspendedBanner,
} from '@pc/shop-ui';
import { BrandLockup, Drawer, Rule, ToastProvider } from '@pc/ui';
import { useRouteTracking } from '@pc/analytics-client';
import { helpUnreadCountForCaller } from '@pc/db';
import { useAuth } from '../auth/AuthProvider';
import { useVendorContext } from '../auth/VendorContext';
import { useVendorSuspensionStatus } from '../hooks/useVendorSuspensionStatus';

interface NavItem {
  to: string;
  // i18n key under `sell.shell.nav.*`. Resolved at render time so the
  // labels track the active locale instead of being frozen at module
  // load.
  labelKey: string;
  end?: boolean;
}

// Desktop split: PRIMARY_NAV renders inline as tabs; MORE_NAV lives behind
// a "More ▾" dropdown so the bar reads calmly. The mobile drawer keeps
// the flat union (PRIMARY_NAV ++ MORE_NAV) -- a vertical list doesn't
// benefit from the overflow.
const PRIMARY_NAV: NavItem[] = [
  { to: '/', labelKey: 'sell.shell.nav.dashboard', end: true },
  { to: '/products', labelKey: 'sell.shell.nav.products' },
  { to: '/orders', labelKey: 'sell.shell.nav.orders' },
  { to: '/inbox', labelKey: 'sell.shell.nav.inbox' },
  { to: '/help', labelKey: 'sell.shell.nav.help' },
];

const MORE_NAV: NavItem[] = [
  { to: '/purchase-orders', labelKey: 'sell.shell.nav.purchaseOrders' },
  { to: '/returns', labelKey: 'sell.shell.nav.returns' },
  { to: '/ads', labelKey: 'sell.shell.nav.advertising' },
  { to: '/team', labelKey: 'sell.shell.nav.team' },
  { to: '/profile', labelKey: 'sell.shell.nav.profile' },
];

const NAV: NavItem[] = [...PRIMARY_NAV, ...MORE_NAV];

function useHelpUnread(enabled: boolean) {
  return useQuery({
    queryKey: ['help', 'unread'],
    queryFn: () => helpUnreadCountForCaller(),
    enabled,
    // Poll every 60s to keep the sidebar badge fresh without piling on
    // RPC traffic. Realtime is intentionally not used here -- this is
    // a small badge, not a chat view.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function SellShell() {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { session, signOut } = useAuth();
  // Account-level language preference follows the user to any device.
  useApplyAccountLocale(Boolean(session));
  const { vendor, available, clearActiveVendor } = useVendorContext();
  const hasMultiple = available.length > 1;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const helpUnread = useHelpUnread(!!session);
  const helpUnreadCount = helpUnread.data ?? 0;

  // When the active vendor is suspended we (a) render the persistent
  // banner at the top of every page, (b) grey + disable the primary nav
  // (excluding the Help nav item -- the appeal channel must stay
  // reachable), and (c) let `<SuspendedGate>` further lock down per-page
  // form regions. The server-side guard
  // (`shop.fn_assert_vendor_not_suspended`) is the authoritative gate;
  // these client affordances are explanatory.
  const suspension = useVendorSuspensionStatus();

  // Self-hosted analytics: fire one pageview per route change for the
  // signed-in vendor. The active vendor slug stamps every event so the
  // SDFs can attribute per-vendor traffic without re-resolving the
  // user→vendor mapping on the server side.
  useRouteTracking({ vendor_slug: vendor?.slug ?? undefined, paused: !session });

  // Close the mobile drawer on route change so navigating from inside the
  // drawer doesn't leave it stuck open.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  function onSwitchStorefront() {
    clearActiveVendor();
    nav('/choose-storefront');
  }

  return (
    <ToastProvider>
      {vendor?.id ? (
        <ShopMessageNotificationWatcher
          side="vendor"
          vendorId={vendor.id}
          inboxBasePath="/inbox"
        />
      ) : null}
      <div className="min-h-screen flex flex-col bg-paper">
        <header className="sticky top-0 z-30 bg-paper/90 backdrop-blur border-b border-rule">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4 pt-[env(safe-area-inset-top)]">
            {/* Mobile hamburger -- only below lg. */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('sell.shell.openNav')}
              aria-expanded={drawerOpen}
              className="lg:hidden -ml-2 p-2 text-ink"
            >
              <HamburgerIcon />
            </button>

            <BrandLockup app="sell" className="shrink-0" />

            {/* Desktop nav -- only lg+. Padding + bottom-border active state
                so the bar reads as a row of tabs rather than dense text.
                When suspended every nav item EXCEPT /help is greyed +
                non-interactive; Help has to stay reachable so the vendor
                can appeal. We grey the items individually rather than
                the whole <nav> wrapper so Help reads as fully enabled.
                Five inline tabs + a "More ▾" dropdown for the overflow
                surfaces (Purchase orders / Returns / Advertising /
                Team / Profile) -- inlining all 10 makes the bar feel
                crammed (operator feedback). */}
            <nav className="hidden lg:flex items-center gap-1 ml-6">
              {PRIMARY_NAV.map((n) => (
                <DesktopNavLink
                  key={n.to}
                  item={n}
                  badgeCount={n.to === '/help' ? helpUnreadCount : 0}
                  greyed={suspension.isSuspended && n.to !== '/help'}
                />
              ))}
              <MoreMenu items={MORE_NAV} suspended={suspension.isSuspended} />
            </nav>

            <div className="ml-auto">
              <AccountMenu
                email={session?.user.email ?? null}
                vendorLabel={
                  vendor
                    ? { slug: vendor.slug, status: vendor.status }
                    : null
                }
                hasMultiple={hasMultiple}
                onSwitchStorefront={onSwitchStorefront}
                onSignOut={signOut}
              />
            </div>
          </div>
        </header>

        {/* Mobile drawer with the same nav items + an account block at
            the bottom so the user can reach everything without leaving. */}
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          side="left"
          ariaLabel={t('sell.shell.sellNavLabel')}
        >
          <div className="shrink-0 px-5 pt-6 pb-4">
            <BrandLockup app="sell" />
          </div>
          <Rule />
          <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3 flex flex-col gap-0.5">
            {NAV.map((n) => {
              const greyed = suspension.isSuspended && n.to !== '/help';
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setDrawerOpen(false)}
                  // tabIndex -1 + aria-disabled when greyed so screen
                  // readers + keyboard nav don't pretend the link is
                  // live. pointer-events-none + opacity-60 mirror the
                  // <SuspendedGate> form treatment.
                  tabIndex={greyed ? -1 : undefined}
                  aria-disabled={greyed || undefined}
                  className={({ isActive }) =>
                    `px-3 py-2.5 text-sm rounded-sm flex items-center justify-between gap-2 ${
                      greyed
                        ? 'opacity-60 pointer-events-none text-muted'
                        : isActive
                          ? 'text-ink font-medium bg-paper-2'
                          : 'text-muted hover:text-ink hover:bg-paper-2'
                    }`
                  }
                >
                  <span>{t(n.labelKey)}</span>
                  {n.to === '/help' && helpUnreadCount > 0 ? (
                    <UnreadDot count={helpUnreadCount} />
                  ) : null}
                </NavLink>
              );
            })}
          </nav>
          <div className="shrink-0 border-t border-rule px-5 py-4 flex flex-col gap-3">
            {vendor ? (
              <div className="text-xs text-muted leading-relaxed">
                {t('sell.shell.storefront')}
                <div className="mt-0.5 text-ink tabular">
                  @{vendor.slug}{' '}
                  <StatusDot status={vendor.status} />
                </div>
                {hasMultiple ? (
                  <button
                    type="button"
                    onClick={onSwitchStorefront}
                    className="mt-1 text-xs text-clay underline-offset-2 hover:underline"
                  >
                    {t('sell.shell.switchStorefront')}
                  </button>
                ) : null}
              </div>
            ) : null}
            <p className="text-xs text-muted truncate">
              {session?.user.email}
            </p>
            <button
              type="button"
              onClick={signOut}
              className="text-xs text-clay text-left underline-offset-2 hover:underline"
            >
              {t('actions.signOut')}
            </button>
          </div>
        </Drawer>

        {suspension.isSuspended ? (
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-4">
            <VendorSuspendedBanner reason={suspension.reason} helpHref="/help" />
          </div>
        ) : null}

        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </main>
        {/*
          Vendor portal footer. The legal links point at the canonical
          pages hosted on the docs site (docs.peptideclients.com) per
          the architectural decision that legal pages live ONCE. The
          destination is cross-origin to this SPA so regular <a href>
          is correct -- do NOT swap these for react-router <Link>.
        */}
        <footer className="border-t border-rule py-6 text-xs text-muted">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-[env(safe-area-inset-bottom)]">
            <p>{t('app.footer.copyright')}</p>
            <p>
              <a href="https://docs.peptideclients.com/terms" className="hover:underline">
                {t('app.footer.terms')}
              </a>{' '}
              ·{' '}
              <a href="https://docs.peptideclients.com/privacy" className="hover:underline">
                {t('app.footer.privacy')}
              </a>{' '}
              ·{' '}
              <a
                href="https://docs.peptideclients.com/research-use-only"
                className="hover:underline"
              >
                {t('app.footer.researchUseOnly')}
              </a>{' '}
              ·{' '}
              <a
                href="https://docs.peptideclients.com/acceptable-use"
                className="hover:underline"
              >
                {t('app.footer.acceptableUse')}
              </a>
            </p>
            <FooterLanguagePicker />
          </div>
        </footer>
      </div>
    </ToastProvider>
  );
}

// -------------------------------------------------------------------------

function DesktopNavLink({
  item,
  badgeCount = 0,
  greyed = false,
}: {
  item: NavItem;
  badgeCount?: number;
  /**
   * When true the link renders at 60% opacity + ignores clicks. Used
   * by the vendor-suspension lockdown to grey every nav item EXCEPT
   * the Help item (which stays interactive so the vendor can appeal).
   */
  greyed?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={item.to}
      end={item.end}
      tabIndex={greyed ? -1 : undefined}
      aria-disabled={greyed || undefined}
      title={greyed ? t('sell.shell.accountSuspended') : undefined}
      className={({ isActive }) =>
        `relative px-3 py-2 text-sm whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
          greyed
            ? 'opacity-60 pointer-events-none text-muted'
            : isActive
              ? 'text-ink font-medium'
              : 'text-muted hover:text-ink'
        } after:absolute after:left-3 after:right-3 after:-bottom-px after:h-px after:transition-colors ${
          !greyed && isActive ? 'after:bg-ink' : 'after:bg-transparent'
        }`
      }
    >
      <span>{t(item.labelKey)}</span>
      {badgeCount > 0 ? <UnreadDot count={badgeCount} /> : null}
    </NavLink>
  );
}

// =========================================================================
// MoreMenu -- the desktop overflow dropdown for lower-frequency nav items.
//
// Mirrors AccountMenu's open/close mechanics (click-outside + Escape +
// route-change auto-close) so the two right-aligned dropdowns feel like
// the same control. The trigger renders the same tab styling as
// DesktopNavLink so it sits flush in the nav row -- including the active
// underline when the current route lives inside the dropdown. When the
// vendor is suspended every item in the dropdown is greyed (none of the
// MORE_NAV items are the Help appeal channel, which stays inline).
// =========================================================================
function MoreMenu({
  items,
  suspended,
}: {
  items: NavItem[];
  suspended: boolean;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const activeInMenu = items.some((it) =>
    it.end
      ? location.pathname === it.to
      : location.pathname === it.to ||
        location.pathname.startsWith(it.to + '/'),
  );

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`relative px-3 py-2 text-sm whitespace-nowrap transition-colors inline-flex items-center gap-1 ${
          activeInMenu ? 'text-ink font-medium' : 'text-muted hover:text-ink'
        } after:absolute after:left-3 after:right-3 after:-bottom-px after:h-px after:transition-colors ${
          activeInMenu ? 'after:bg-ink' : 'after:bg-transparent'
        }`}
      >
        <span>{t('sell.shell.more')}</span>
        <ChevronDownIcon className="w-3.5 h-3.5" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 bg-paper border border-rule shadow-sm py-1 z-40"
        >
          {items.map((it) => {
            const greyed = suspended;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                role="menuitem"
                tabIndex={greyed ? -1 : undefined}
                aria-disabled={greyed || undefined}
                title={greyed ? t('sell.shell.accountSuspended') : undefined}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm ${
                    greyed
                      ? 'opacity-60 pointer-events-none text-muted'
                      : isActive
                        ? 'text-ink font-medium bg-paper-2'
                        : 'text-ink hover:bg-paper-2'
                  }`
                }
              >
                {t(it.labelKey)}
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function UnreadDot({ count }: { count: number }) {
  const { t } = useTranslation();
  return (
    <span
      aria-label={t('sell.shell.unread', { count })}
      className="inline-grid place-items-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-medium bg-clay text-paper rounded-full tabular-nums"
    >
      {count > 9 ? t('sell.shell.unreadOverflow') : count}
    </span>
  );
}

function HamburgerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
      className="w-6 h-6"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className ?? 'w-3.5 h-3.5'}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function StatusDot({ status }: { status: string }) {
  const tone =
    status === 'approved'
      ? 'bg-[#16742D]'
      : status === 'pending'
        ? 'bg-clay'
        : 'bg-muted';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted ml-1">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone}`} />
      {status}
    </span>
  );
}

// =========================================================================
// AccountMenu -- the lone right-side control.
//
// Click the initial circle to open a dropdown that holds the email, the
// storefront pill, the "Switch storefront" shortcut (when relevant), and
// "Sign out". This used to be FOUR separate elements (storefront pill +
// switch icon + sign-out button + email) competing with the primary nav;
// folding them into one trigger is what makes the bar feel calm again.
// =========================================================================
interface AccountMenuProps {
  email: string | null;
  vendorLabel: { slug: string; status: string } | null;
  hasMultiple: boolean;
  onSwitchStorefront(): void;
  onSignOut(): void;
}

function AccountMenu({
  email,
  vendorLabel,
  hasMultiple,
  onSwitchStorefront,
  onSignOut,
}: AccountMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = initialsFromEmail(email);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-paper-2 transition-colors"
      >
        <span className="grid place-items-center w-8 h-8 rounded-full bg-ink text-paper text-xs font-medium tabular">
          {initials}
        </span>
        <ChevronDownIcon className="w-3.5 h-3.5 text-muted hidden sm:inline" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-72 bg-paper border border-rule shadow-sm py-2 z-40"
        >
          <div className="px-4 py-2">
            <p className="smallcap text-[10px] text-muted">{t('sell.shell.signedInAs')}</p>
            <p className="text-sm text-ink truncate">{email ?? '—'}</p>
          </div>
          {vendorLabel ? (
            <>
              <Rule />
              <div className="px-4 py-2">
                <p className="smallcap text-[10px] text-muted">{t('sell.shell.storefront')}</p>
                <p className="text-sm text-ink tabular">
                  @{vendorLabel.slug}
                  <StatusDot status={vendorLabel.status} />
                </p>
                {hasMultiple ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onSwitchStorefront();
                    }}
                    className="mt-2 text-xs text-clay underline-offset-2 hover:underline"
                  >
                    {t('sell.shell.switchStorefront')}
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
          <Rule />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-paper-2"
          >
            {t('actions.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function initialsFromEmail(email: string | null): string {
  if (!email) return '·';
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[.\-_+]/).filter(Boolean);
  if (parts.length === 0) return email.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
